import './signUpPinVerifyPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import isEmpty from 'lodash/isEmpty';
import uniqueId from 'lodash/uniqueId';
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { CheckCircleIcon } from '../../components/Icons/CheckCircleIcon';
import { ErrorOutlineIcon } from '../../components/Icons/ErrorOutlineIcon';
import { OTP } from '../../components/OTP/OTP';
import { Toast } from '../../components/Toast/Toast';
import { resetStore } from '../../store/_core/resetStore';
import useApiStore from '../../store/apiStore/useApiStore';
import { HydrateSource } from '../../store/applyStore/enums/HydrateSource';
import useApplyStore from '../../store/applyStore/useApplyStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import { useCountdownTimer } from '../../store/pinStore/hooks/useCountdownTimer';
import usePinStore from '../../store/pinStore/usePinStore';
import { useNavigateWithCallback } from '../../store/routeStore/hooks/useNavigateWithCallback';
import useSignUpStore from '../../store/signUpStore/useSignUpStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { t } = useTranslation();
  const { isLoading } = useApiStore();
  const { applicationType, leadChannelCode, vehicle_vin } = useParameterStore();
  const { hydrate } = useApplyStore();
  const {
    clearPin,
    deliverPin,
    deliverPinFailedMessage,
    deliverPinRetrySuccess,
    pin,
    setPin,
    computed: { hasValidPin },
  } = usePinStore();
  const { firstName, lastName, primaryPhoneNumber, primaryEmailAddress } =
    useSignUpStore();
  const {
    clearPinPurpose,
    clearPinRetryState,
    setUserFirstName,
    setUserLastName,
    setUserSignedUp,
    userAuthenticate,
    userCreate,
    userUpdate,
    userCreateFailedMessage,
    userAuthenticateFailedMessage,
    computed: { shouldCreateUser, isPinPurposeEmpty },
    pinPurpose,
    getDeliverPinParams,
  } = useUserStore();

  const resendTimerDuration = 15; // in seconds
  const [seconds, setSecondsLeft] = useState(0);
  const { startTimer, stopTimer } = useCountdownTimer(
    setSecondsLeft,
    resendTimerDuration,
  );

  const buttonText = useMemo((): string => {
    if (seconds > 0) {
      return t('SignUpPinVerifyPage.resendCodeDelay', {
        delay: seconds,
      });
    }
    return t('SignUpPinVerifyPage.resendCode');
  }, [t, seconds]);

  const formSubmit = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    if (!pin) {
      return;
    }
    // Set before creating/authenticating so SessionGuard does not briefly redirect
    // the still-authenticating user away from this non-authenticated route.
    setUserSignedUp(true);
    if (shouldCreateUser) {
      await userCreate(
        firstName,
        lastName,
        primaryEmailAddress,
        primaryPhoneNumber,
        pin,
      );
    } else {
      await userAuthenticate(
        primaryPhoneNumber,
        pin,
        leadChannelCode,
        pinPurpose,
      );

      // only update the profile once signed in: without a session the PATCH
      // is a 401, which redirects away from the error just shown
      if (useUserStore.getState().userAuthenticateSuccess) {
        setUserFirstName(firstName);
        setUserLastName(lastName);
        await userUpdate();
      }
    }
    const { userAuthenticateSuccess, userCreateSuccess } =
      useUserStore.getState();
    if (userCreateSuccess || userAuthenticateSuccess) {
      const applyRoute = await hydrate({
        applicationType,
        source: HydrateSource.SIGN_UP,
        vin: vehicle_vin,
      });
      navigateWithCallback(applyRoute).then((): void => {
        resetStore(useSignUpStore);
        clearPinPurpose();
      });
    }
  };

  const resendCode = async (): Promise<void> => {
    if (primaryPhoneNumber) {
      startTimer();
      clearPinRetryState();
      const deliverPinParams = getDeliverPinParams(
        DeliverPinLocation.RETRY,
        primaryPhoneNumber,
        primaryEmailAddress,
      );
      await deliverPin(deliverPinParams);
      clearPin();
    }
  };

  useEffect((): (() => void) => {
    if (!primaryPhoneNumber || isPinPurposeEmpty) {
      navigate('/sign-up');
    }
    startTimer();
    return (): void => {
      stopTimer();
    };
  }, [primaryPhoneNumber]);

  useEffect((): void => {
    if (userCreateFailedMessage || userAuthenticateFailedMessage) {
      clearPin();
    }
  }, [userCreateFailedMessage, userAuthenticateFailedMessage]);

  useEffect((): (() => void) => {
    // Clearing pin on unmount so the next pin page starts fresh
    return (): void => {
      clearPin();
    };
  }, []);

  useEffect((): void => {
    // Auto-submit once the last digit is entered
    if (hasValidPin) {
      formSubmit();
    }
  }, [hasValidPin]);

  return (
    <div className="sign-up-pin-verify-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Toast
            open={deliverPinRetrySuccess}
            onClose={(): void => {
              usePinStore.setState({ deliverPinRetrySuccess: false });
            }}
          >
            <Alert
              data-testid={locator.pages.signUpPinVerify.alert.success}
              icon={<CheckCircleIcon />}
              severity="success"
            >
              {t('SignUpPinVerifyPage.codeSent')}
            </Alert>
          </Toast>

          <Toast
            open={!isEmpty(deliverPinFailedMessage)}
            onClose={(): void => {
              usePinStore.setState({ deliverPinFailedMessage: null });
            }}
          >
            <Alert
              data-testid={locator.pages.signUpPinVerify.alert.error}
              icon={<ErrorOutlineIcon />}
              severity="error"
            >
              {deliverPinFailedMessage}
            </Alert>
          </Toast>

          <Header
            trailing={
              <Link
                data-testid={locator.pages.signUpPinVerify.header.signUpLink}
                to="/sign-up"
              >
                {t('SignUpPinVerifyPage.signup')}
              </Link>
            }
            showLogo
          />

          <main>
            <figure>
              <img
                alt={t('imageAlts.heroReference')}
                src={heroReferenceMessage}
              />
            </figure>
            <form noValidate onSubmit={formSubmit}>
              <div className="form-content">
                <h1 data-testid={locator.pages.signUpPinVerify.form.h1}>
                  {t('SignUpPinVerifyPage.title')}
                </h1>
                <h3 data-testid={locator.pages.signUpPinVerify.form.h3}>
                  <Trans
                    components={[
                      <span
                        key={uniqueId('span-')}
                        style={{ textDecoration: 'underline' }}
                      />,
                    ]}
                    i18nKey={'SignUpPinVerifyPage.subtitle'}
                    values={{
                      phoneNumber: toPhoneString(primaryPhoneNumber),
                    }}
                  />
                </h3>

                <OTP
                  disabled={isLoading}
                  error={
                    userCreateFailedMessage || userAuthenticateFailedMessage
                  }
                  errorTestId={locator.pages.signUpPinVerify.form.pinInputError}
                  inputTestId={locator.pages.signUpPinVerify.form.pinInput}
                  value={pin}
                  onChange={setPin}
                />

                <Button
                  data-testid={locator.pages.signUpPinVerify.form.resendPinLink}
                  disabled={seconds > 0}
                  loader={isLoading}
                  fullWidth
                  onClick={resendCode}
                >
                  {buttonText}
                </Button>
              </div>
              <footer data-testid={locator.pages.signUpPinVerify.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'SignUpPinVerifyPage';
